// The Director's stage (spec-v4 sections 5.3, 5.4, 8.4): one main stage during a call. Phones: a full-screen layer
// with the call on top ("● Key Light · 2:14 · 30/min  ▾  ✕"), the item, the Director's last line as a caption, the
// conversation in a fold and the light / Mute / End at the foot. Desktop: the main pane, with the conversation as
// the right column; on the video's own suite the preview already is the stage, so there's no overlay (Q11).
// StageLayer (mounted once in App) opens it when a call connects, closes it when the call ends, and draws the call
// banner where the Director bar doesn't.
import { useEffect, useRef } from "react";
import { CallMeter, DirectorMark, MuteButton, useShownStatus } from "../components/Director";
import { usePersona } from "../components/DirectorPicker";
import { Icon } from "../components/bits";
import { Fold } from "../components/Fold";
import { PlaybackSurface } from "../components/Stage";
import { Composer, isLive, toggleTalk, Transcript } from "../components/Talk";
import { director } from "../director";
import { isActive } from "../playback";
import { useLocation } from "../router";
import { useStore } from "../store";
import { AuditionView } from "./Audition";
import { CallBanner } from "./CallBanner";
import { ChoicesView } from "./Choices";
import { dispatchStage, focusStage, minimiseStage, stageBack } from "./actions";
import { useStageLoad } from "./data";
import { ItemView } from "./items";
import { focusedItem, itemLabel, screenItems, stageTitle } from "./state";
import { useDesktop, useStageSurface } from "./surface";
import "./stage.css";

const CONNECTED = new Set(["listening", "thinking", "speaking"]);

/** Mounted once in App: opens and closes the stage with the call, and draws it (or the banner) where it belongs. */
/** "the video" -> "The video" on the back button (X3) */
const upFirst = (t: string) => (t ? t[0]!.toUpperCase() + t.slice(1) : t);

export function StageLayer() {
  const status = useStore((s) => s.director);
  const switching = useStore((s) => !!s.directorSwitching);
  const live = CONNECTED.has(status) || (switching && status !== "error");
  const { path } = useLocation();
  const desktop = useDesktop();
  const surface = useStageSurface();
  const suiteProject = useStore((s) => s.suiteProject);
  const was = useRef(false);
  useEffect(() => {
    if (live === was.current) return;
    was.current = live;
    // the call opens on what's on screen; a voice switch reconnects without closing it
    dispatchStage(live ? { type: "call_start", items: screenItems(path) } : { type: "call_end" });
  }, [live]); // eslint-disable-line react-hooks/exhaustive-deps

  // phones: the stage is the page while it's up
  useEffect(() => {
    if (surface !== "overlay" || desktop) return;
    document.body.classList.add("is-staged");
    return () => document.body.classList.remove("is-staged");
  }, [surface, desktop]);

  if (surface === "overlay") return <StageView desktop={desktop} />;
  if (surface !== "banner") return null;
  // the Director bar carries the banner on phones (components/Director.tsx); Home has no bar, and desktop puts it
  // at the top of the rail column
  // (the suite's own Director panel carries it there)
  if (desktop)
    return suiteProject ? null : (
      <div className="cbanner-slot cbanner-slot-rail">
        <CallBanner variant="rail" />
      </div>
    );
  return path === "/" ? (
    <div className="cbanner-slot">
      <CallBanner />
    </div>
  ) : null;
}

/** The Director's latest line, as the stage's caption. */
function useCaption() {
  return useStore((s) => [...s.transcript].reverse().find((l) => l.who === "director" && !l.artifact)?.text ?? null);
}

function CallHead({ desktop }: { desktop: boolean }) {
  const status = useShownStatus();
  const persona = usePersona();
  return (
    <p className="stg-call">
      <span className="stg-dot" aria-hidden />
      <span className="stg-call-mark" aria-hidden>
        <DirectorMark status={status} />
      </span>
      <strong>{persona.name}</strong>
      <CallMeter className="stg-meter" />
      {!desktop && status === "thinking" && <span className="stg-call-doing">Working</span>}
    </p>
  );
}

/** End: hang up when a call is on; with no call (a stage a tap opened), just close the stage. */
function endOrClose(): void {
  director.stop();
  dispatchStage({ type: "call_end" }); // a no-op when nothing is open; the live path's own call_end is the same
}

function CallControls() {
  const status = useShownStatus();
  const live = isLive(status);
  const typed = useStore((s) => s.directorTyped);
  return (
    <div className="stg-controls">
      <button data-action="call.talk" type="button" className="stg-light" onClick={() => toggleTalk(status)} aria-label={typed && live ? "Talk out loud instead" : live ? "End the call with your Director" : "Talk to your Director"} aria-pressed={live}>
        <DirectorMark status={status} />
      </button>
      <MuteButton />
      <button data-action="call.end" type="button" className="btn btn-quiet stg-end" onClick={endOrClose}>
        <Icon name="stop" size={14} /> End
      </button>
    </div>
  );
}

export function StageView({ desktop }: { desktop: boolean }) {
  const stage = useStore((s) => s.stage);
  const pb = useStore((s) => s.playback);
  const suiteProject = useStore((s) => s.suiteProject);
  const caption = useCaption();
  const said = useStore((s) => s.transcript.length);
  const item = focusedItem(stage);
  // a video on stage with no title of its own is called by its name ("Pip and the Dark", not "the video")
  const named = !stage.title && item && !item.title && (item.kind === "project" || item.kind === "video") ? item.projectId : null;
  const { data: namedProject } = useStageLoad(named ? `project:${named}` : null, (src) => src.project(named!));

  useEffect(() => {
    const key = (e: KeyboardEvent) => e.key === "Escape" && !e.defaultPrevented && minimiseStage();
    window.addEventListener("keydown", key);
    return () => window.removeEventListener("keydown", key);
  }, []);

  if (!item) return null;
  const title = named && namedProject ? namedProject.title : stageTitle(stage);
  const playingVideo = isActive(pb) && pb.req && pb.req.what !== "audio" && pb.req.surface !== "library";
  const multi = stage.items.length > 1;
  const count = item.kind === "audition" ? `${Math.min(stage.focus + 1, item.candidates.length)} of ${item.candidates.length}` : multi ? `${stage.focus + 1} of ${stage.items.length}` : null;
  // desktop over a video's suite: the stage is about something else, and ▾ goes back to the video
  const minLabel = desktop && suiteProject ? "Back to this video" : "Minimise";

  let body;
  if (playingVideo) body = <PlaybackSurface pb={pb} />;
  else if (item.kind === "choices") body = <ChoicesView item={item} />;
  else if (item.kind === "audition") body = <AuditionView item={item} focus={Math.min(stage.focus, item.candidates.length - 1)} />;
  else body = <ItemView it={item} />;

  return (
    <div className={`stg${desktop ? " stg-desk" : ""} stg-kind-${playingVideo ? "playing" : item.kind}`} role="region" aria-label="Your Director's stage">
      <div className="stg-main">
        <header className="stg-head">
          {!desktop && <CallHead desktop={false} />}
          {desktop && stage.history.length === 0 && <span className="stg-crumb">With your Director</span>}
          {desktop && stage.history.length > 0 && (
            <button data-action="stage.back" type="button" className="stg-back" onClick={stageBack}>
              <Icon name="back" size={16} /> {upFirst(itemLabel(focusedItem(stage.history.at(-1)!)))}
            </button>
          )}
          <span className="stg-head-btns">
            {desktop ? (
              <button data-action="stage.minimise" type="button" className="btn btn-quiet btn-small stg-min" onClick={minimiseStage}>
                <Icon name="chevron" size={16} /> {minLabel}
              </button>
            ) : (
              <>
                <button data-action="stage.minimise" type="button" className="icon-btn stg-min" onClick={minimiseStage} aria-label="Minimise the stage (the call stays on)" title="Minimise">
                  <Icon name="chevron" size={20} />
                </button>
                <button data-action="call.end" type="button" className="icon-btn stg-x" onClick={endOrClose} aria-label="Close (ends the call if one is on)" title="Close">
                  <Icon name="close" size={20} />
                </button>
              </>
            )}
          </span>
        </header>

        <div className="stg-titlebar">
          {!desktop && stage.history.length > 0 && (
            <button data-action="stage.back" type="button" className="icon-btn stg-back-icon" onClick={stageBack} aria-label="Back to what was on stage before">
              <Icon name="back" size={18} />
            </button>
          )}
          <h2 className="stg-title">{playingVideo ? (pb.req?.title ?? "Playing") : title[0]!.toUpperCase() + title.slice(1)}</h2>
          {count && !playingVideo && <span className="stg-count">{count}</span>}
        </div>

        {multi && !playingVideo && (
          <div className="stg-tabs stg-items" role="tablist" aria-label="On stage">
            {stage.items.map((it, i) => (
              <button data-action="stage.focus" key={i} type="button" role="tab" aria-selected={i === stage.focus} className={`stg-tab${i === stage.focus ? " is-on" : ""}`} onClick={() => focusStage(i)}>
                {itemLabel(it)}
              </button>
            ))}
          </div>
        )}

        <div className="stg-body">{body}</div>

        {!desktop && (
          <>
            {caption && <p className="stg-caption">“{caption}”</p>}
            {/* v4 (V4-P2): the phone's conversation is a Fold ("stage.conversation"), shut until they open it */}
            <Fold id="stage.conversation" title="Conversation" count={said || undefined} className="stg-talk">
              <div className="stg-talk-body">
                <Transcript limit={8} compact />
                <Composer placeholder="Type to your Director" />
              </div>
            </Fold>
            <CallControls />
          </>
        )}
      </div>

      {desktop && (
        <aside className="stg-side" aria-label="The conversation">
          <CallHead desktop />
          <Transcript limit={12} compact />
          <Composer placeholder="Type to your Director" />
          <CallControls />
        </aside>
      )}
    </div>
  );
}
